import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { getSettings } from '../../queries.js';
import { zql } from '../../schema.js';
import { inUseGfrQuery } from '../../util/patient-gfr.js';
import { addDaysUtc, daysBetweenTimestamps, EventLogs, isoDateFromTimestamp, timestampFromIsoDate } from '../../utils.js';
import {} from '../patient.mutators.js';
import { addEventLog, EventLogResources } from '../util.js';
import { itemsDetail } from '../../util/event-log-items.js';
import { sequenceLogItems } from './medicine-log-items.js';
import { buildCycleTherapySequences, finalizeCycleTherapySequences, insertCycleTherapySequences, } from './assign-patient-protocol.js';
import { logAmmsMfnChangeForSequence } from './add-therapy-parameter.js';
import { canShiftTherapySequence } from './update-therapy-parameter.js';
function getEarliestTime(times) {
    return (times.reduce((earliest, time) => (earliest === undefined || time < earliest ? time : earliest), undefined) ??
        '00:00');
}
export async function updatePatientProtocolCycles(tx, input, userId) {
    const now = Date.now();
    const patientProtocol = await tx.run(zql.patientProtocols.where('id', '=', input.patientProtocolId).where('deletedAt', 'IS', null).one());
    if (!patientProtocol)
        throw new Error('Not Found');
    if (patientProtocol.endDate != null)
        throw new Error('A stopped protocol cannot be edited. Reactivate it first.');
    const protocolStartIso = isoDateFromTimestamp(patientProtocol.startDate);
    for (const c of input.cycles) {
        const startIso = isoDateFromTimestamp(c.startDate);
        // Only edited or new cycles: an untouched legacy cycle must not block saving other changes.
        const isEdited = c.sourceCycle == null || patientProtocol.cycleStartDates?.[c.sourceCycle] !== startIso;
        if (isEdited && startIso < protocolStartIso) {
            throw new Error(`Cycle ${c.cycle} cannot start before the protocol start date`);
        }
    }
    const protocol = await tx.run(zql.protocols.where('id', '=', patientProtocol.protocolId).where('deletedAt', 'IS', null).one());
    if (!protocol)
        throw new Error('Not Found');
    const characteristics = await tx.run(zql.patientCharacteristics
        .related('patient')
        .where('patientId', '=', patientProtocol.patientId)
        .orderBy('createdAt', 'desc')
        .limit(1)
        .one());
    const existingSequences = await tx.run(zql.patientTherapySequence
        .related('medicine', medicine => medicine.related('activeSubstance').related('prescribedName'))
        .where('patientProtocolId', '=', input.patientProtocolId)
        .where('deletedAt', 'IS', null));
    const sequenceOps = [];
    const logSequenceOps = [];
    for (const deletedCycle of input.deletedCycles) {
        const sequencesToCancel = existingSequences.filter(seq => seq.cycle === deletedCycle);
        for (const seq of sequencesToCancel) {
            if (!canShiftTherapySequence(seq)) {
                throw new Error(`Cannot delete cycle ${deletedCycle}: a prescription is already in production`);
            }
        }
    }
    // Before the medicines are soft-deleted, so each MFN MDL still carries its ZDRs.
    await Promise.all(existingSequences
        .filter(seq => seq.cycle != null && input.deletedCycles.includes(seq.cycle))
        .map(seq => logAmmsMfnChangeForSequence(tx, { sequenceId: seq.id, change: 'delete', now })));
    for (const deletedCycle of input.deletedCycles) {
        const sequencesToCancel = existingSequences.filter(seq => seq.cycle === deletedCycle);
        for (const seq of sequencesToCancel) {
            sequenceOps.push(() => tx.mutate.patientTherapySequence.update({ id: seq.id, deletedAt: now, deletedBy: userId }));
            for (const med of seq.medicine) {
                sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.update({ id: med.id, deletedAt: now, deletedBy: userId }));
            }
            if (tx.location === 'server') {
                const patches = createPatch(seq, { ...seq, deletedAt: now, deletedBy: userId });
                logSequenceOps.push(() => addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.patientTherapySequence,
                    resourceId: seq.id,
                    action: 'delete',
                    domain: 'prescription',
                    event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_DELETED,
                    patches,
                    performedBy: userId,
                    performedAt: now,
                    details: {
                        id: seq.internalId,
                        patientId: patientProtocol.patientId,
                        patientName: characteristics?.patient?.fullName ?? '',
                        reason: input.reason,
                    },
                }));
            }
        }
    }
    const sourceCycleMap = new Map(input.cycles
        .filter((c) => c.sourceCycle != null)
        .map(c => [c.sourceCycle, c]));
    for (const seq of existingSequences) {
        if (seq.cycle == null || input.deletedCycles.includes(seq.cycle))
            continue;
        const target = sourceCycleMap.get(seq.cycle);
        if (!target)
            continue;
        const cycleChanged = target.cycle !== seq.cycle;
        const storedStartIso = patientProtocol.cycleStartDates?.[seq.cycle];
        const deltaDays = storedStartIso ? daysBetweenTimestamps(timestampFromIsoDate(storedStartIso), target.startDate) : 0;
        const moved = deltaDays !== 0;
        if (!cycleChanged && !moved)
            continue;
        if (moved && !canShiftTherapySequence(seq)) {
            throw new Error(`Cannot move cycle ${seq.cycle}: a prescription is already in production`);
        }
        const newTherapyDate = moved ? addDaysUtc(seq.therapyDate, deltaDays) : seq.therapyDate;
        // Editing a doctor-validated prescription (status `validating`) reverses the validation:
        // clear the validating doctor and return the sequence to `pending_doctor_validation` so
        // it must be re-validated - same convention as update-therapy-parameter.ts.
        const wasDoctorValidated = seq.status === PatientTherapySequenceStatus.enum.validating;
        const update = {
            id: seq.id,
            ...(cycleChanged && { cycle: target.cycle }),
            ...(moved && { therapyDate: newTherapyDate }),
            updatedAt: now,
            updatedBy: userId,
            ...(moved &&
                wasDoctorValidated && {
                status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
                doctorValidatedBy: null,
                doctorValidatedAt: null,
                pharmacistValidatedAt: null,
            }),
        };
        sequenceOps.push(() => tx.mutate.patientTherapySequence.update(update));
        if (moved) {
            for (const med of seq.medicine) {
                sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.update({
                    id: med.id,
                    therapyDate: newTherapyDate,
                    updatedAt: now,
                    updatedBy: userId,
                }));
            }
        }
        if (tx.location === 'server') {
            const patches = createPatch(seq, { ...seq, ...update });
            logSequenceOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: seq.id,
                action: 'update',
                domain: 'prescription',
                event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    therapyInternalId: seq.internalId,
                    patientId: patientProtocol.patientId,
                    patientName: characteristics?.patient?.fullName ?? '',
                    reason: input.reason,
                    ...itemsDetail(sequenceLogItems(seq, update)),
                },
            }));
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const newCycles = input.cycles.filter(c => c.sourceCycle == null).map(c => ({ cycle: c.cycle, startDate: c.startDate }));
    if (newCycles.length) {
        const protocolSequences = await tx.run(zql.protocolTherapySequence
            .related('medicine', medicine => medicine
            .related('activeSubstance', drug => drug.related('activeSubstanceDetails').related('supportiveMedicationDetails'))
            .related('prescribedName', pn => pn.related('drugCatalogItem', item => item.related('activeSubstanceDetails'))))
            .where('protocolId', '=', patientProtocol.protocolId)
            .where('deletedAt', 'IS', null));
        const gfr = await tx.run(inUseGfrQuery(patientProtocol.patientId));
        const settings = await getSettings(tx);
        const isZeroDayProtocol = settings?.dayZeroProtocol;
        const startTime = getEarliestTime(existingSequences.length ? existingSequences.map(seq => seq.time) : protocolSequences.map(seq => seq.time));
        const build = buildCycleTherapySequences({
            patientId: patientProtocol.patientId,
            protocolId: patientProtocol.protocolId,
            patientProtocolId: input.patientProtocolId,
            protocolSequences,
            characteristics,
            gfr,
            isZeroDayProtocol,
            startTime,
            cycles: newCycles,
            userId,
            now,
        });
        await insertCycleTherapySequences(tx, build);
        await finalizeCycleTherapySequences(tx, {
            therapySequences: build.therapySequences,
            sequenceMedicines: build.sequenceMedicines,
            characteristics,
            userId,
            now,
        });
    }
    const cycleStartDates = {};
    for (const c of input.cycles) {
        cycleStartDates[c.cycle] = isoDateFromTimestamp(c.startDate);
    }
    await tx.mutate.patientProtocols.update({
        id: input.patientProtocolId,
        cycles: input.cycles.length,
        cycleStartDates,
        updatedAt: now,
        updatedBy: userId,
    });
    if (tx.location === 'server') {
        const movedCycles = input.cycles
            .filter((c) => c.sourceCycle != null)
            .map(c => {
            const fromIso = patientProtocol.cycleStartDates?.[c.sourceCycle];
            const toIso = isoDateFromTimestamp(c.startDate);
            return fromIso && fromIso !== toIso ? `${c.cycle}: ${fromIso} → ${toIso}` : null;
        })
            .filter(Boolean);
        const addedCycles = newCycles.map(c => `${c.cycle}: ${isoDateFromTimestamp(c.startDate)}`);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientProtocol,
            resourceId: input.patientProtocolId,
            action: 'update',
            domain: 'patient',
            event: EventLogs.PATIENT_PROTOCOL_CYCLES_UPDATED,
            patches: createPatch({ cycles: patientProtocol.cycles, cycleStartDates: patientProtocol.cycleStartDates }, { cycles: input.cycles.length, cycleStartDates }),
            status: 'OK',
            performedBy: userId,
            performedAt: now,
            details: {
                patientId: patientProtocol.patientId,
                patientName: characteristics?.patient?.fullName ?? '',
                protocolName: protocol.name,
                movedCycles: movedCycles.join(', ') || undefined,
                deletedCycles: input.deletedCycles.join(', ') || undefined,
                addedCycles: addedCycles.join(', ') || undefined,
                reason: input.reason,
            },
        });
    }
}
//# sourceMappingURL=update-patient-protocol-cycles.js.map